<template>
  <button @click="toggleReviewLock">
    <div
      v-tooltip.left="canLockReview.canLock ? '' : canLockReview.message"
      class="px-4 py-3 flex items-center gap-2 hover:bg-light-neutral-500 w-full"
      :class="{
        'text-dark-neutral-200 cursor-not-allowed': !canLockReview.canLock,
      }"
    >
      <component
        :is="latestReview?.isLocked ? ShieldCheckIcon : ShieldCheckIcon"
        class="size-5"
        :class="[
          !canLockReview.canLock
            ? ''
            : latestReview?.isLocked
              ? 'text-red-600'
              : 'text-green-500',
        ]"
      />
      <a class="font-medium relative text-sm">
        {{ latestReview?.isLocked ? 'Unapprove review' : 'Approve review' }}
        <span class="absolute inset-0" />
      </a>
    </div>
  </button>
</template>

<script lang="ts" setup>
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { ReviewsServiceKey } from '@app/injectionKeys'
import { SnackbarState } from '@app/types'
import { injectStrict } from '@app/utils/injectStrict'
import useProjects from '@app/views/Projects/use-projects'
import { HttpException } from '@core/exceptions/http.exception'
import { ShieldCheckIcon } from '@heroicons/vue/24/solid'
import { computed, ref, watch } from 'vue'
const reviewsService = injectStrict(ReviewsServiceKey)
const { reviewSeries } = defineProps<{
  reviewSeries: {
    id: string
    name: string
    order: string | null
    reviews: {
      id: number
      createdAt: Date
      isLocked: boolean
    }[]
  }
}>()
const { refresh } = useProjects()
const loading = useLoading()
const snackbar = useSnackbar()
const latestReview = computed(() => {
  if (reviewSeries.reviews.length === 0) {
    return null
  }
  return reviewSeries.reviews.reduce((latest, review) =>
    review.createdAt > latest.createdAt ? review : latest,
  )
})
async function toggleReviewLock() {
  loading.start()
  try {
    if (latestReview.value?.isLocked) {
      await unlockReview()
    } else if (canLockReview.value.canLock) {
      await lockReview()
    }
  } finally {
    loading.stop()
  }
}

async function unlockReview() {
  if (!latestReview.value) return
  try {
    await reviewsService.unlock(latestReview.value.id)
    refresh()
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while unlocking review, please try again.',
      )
    }
    throw e
  }
}

async function lockReview() {
  if (!latestReview.value) return
  try {
    await reviewsService.lock(latestReview.value.id)
    refresh()
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error?.response?.data?.statusCode >= 400 &&
      error?.response?.data?.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while locking review, please try again.',
      )
    }
    throw e
  }
}

const canLockReview = ref<{ canLock: boolean; message?: string }>({
  canLock: false,
  message: undefined,
})

watch(
  latestReview,
  async () => {
    if (!latestReview.value) return
    canLockReview.value = await reviewsService.canLockReview(
      latestReview.value?.id,
    )
  },
  {
    immediate: true,
  },
)
</script>
